import { Chip, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { InfoItem } from "components/InfoItem/InfoItem";
import { Company } from "generated/graphql";
import React, { useMemo } from "react";
import { getContactInfo } from "utils/contacts/getContactInfo";
import { getFullName } from "utils/people/getFullName";

type Contact = Company["contacts"][number];

function ContactBlock({ contact, isPrimary }: { contact: Contact; isPrimary?: boolean }) {
  const details = useMemo(() => getContactInfo(contact), [contact]);

  // We show Name as a header, so remove it from InfoItem list to avoid duplication.
  const detailsWithoutName = details.filter((d) => d.label.toLowerCase() !== "name");

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
        <MDTypography variant="h6" fontWeight="medium">
          {getFullName(contact)}
        </MDTypography>
        {isPrimary ? <Chip size="small" label="Primary" /> : null}
      </MDBox>

      <MDBox>
        {detailsWithoutName.map((item) => (
          <InfoItem key={`${contact.id}-${item.label}`} item={item} dense />
        ))}
      </MDBox>
    </MDBox>
  );
}

export default function CompanyContactsStacked({
  primaryContact,
  secondaryContacts,
  icon,
}: {
  primaryContact?: Contact | null;
  secondaryContacts: Contact[];
  icon: React.ReactNode;
}) {
  const hasPrimary = !!primaryContact;
  const hasSecondary = secondaryContacts?.length > 0;

  return (
    <MDBox width="100%">
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          Contacts
        </MDTypography>
        {icon}
      </MDBox>

      <MDBox p={2}>
        <MDBox opacity={0.3}>
          <Divider />
        </MDBox>

        <MDBox mt={2}>
          {!hasPrimary ? (
            <MDTypography variant="body2" color="text">
              No contacts yet.
            </MDTypography>
          ) : (
            <ContactBlock contact={primaryContact as Contact} isPrimary />
          )}

          {hasSecondary ? (
            <MDBox mt={2}>
              <MDBox opacity={0.3} mb={2}>
                <Divider />
              </MDBox>

              {secondaryContacts.map((c, idx) => (
                <MDBox key={c.id} mb={idx === secondaryContacts.length - 1 ? 0 : 2}>
                  <ContactBlock contact={c} />
                  {idx !== secondaryContacts.length - 1 ? (
                    <MDBox opacity={0.15} mt={2}>
                      <Divider />
                    </MDBox>
                  ) : null}
                </MDBox>
              ))}
            </MDBox>
          ) : null}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
